import React from 'react';
import { useTranslation } from 'react-i18next';
import HelpIcon from '../help/HelpIcon';
import { buildCmTierRows, signOnSummary } from '../../utils/partnerPortal';

/**
 * Condiciones del programa del partner (RES-93).
 * - CM: escalón actual, cuántos locales faltan para el siguiente y la escala completa. Sin fin.
 * - Influencer: ventana de N meses pagos por local y bonos por alta usados.
 */
function PartnerProgramCard({ partner, metrics, cmTiers }) {
  const { t } = useTranslation();
  const isCm = partner.program === 'cm';

  if (isCm) {
    const cmTier = metrics.cmTier || {};
    const activeLocations = cmTier.activeLocations ?? metrics.activeConversions ?? 0;
    const rows = buildCmTierRows(cmTiers, activeLocations);
    return (
      <section className="pp-card pp-program-card" data-program="cm" aria-labelledby="pp-program">
        <h2 id="pp-program" className="pp-card-title">
          {t('partnerPortal.program.cmTitle')} <HelpIcon topicKey="partners.programs" />
        </h2>
        <p className="pp-program-highlight" data-testid="pp-cm-current">
          <span className="pp-program-pct">{cmTier.pct ?? partner.commissionPct}%</span>
          <span>{t('partnerPortal.program.cmCurrent', { count: activeLocations })}</span>
        </p>
        {cmTier.nextTier ? (
          <p className="pp-program-next" data-testid="pp-cm-next">
            {t('partnerPortal.program.cmNext', {
              count: cmTier.locationsToNextTier,
              pct: cmTier.nextTier.pct,
            })}
          </p>
        ) : (
          <p className="pp-program-next" data-testid="pp-cm-top">
            {t('partnerPortal.program.cmTop')}
          </p>
        )}
        <p className="pp-muted">{t('partnerPortal.program.cmRule')}</p>
        {rows.length > 0 && (
          <table className="pp-table pp-tier-table">
            <thead>
              <tr>
                <th scope="col">{t('partnerPortal.program.tierLocations')}</th>
                <th scope="col">{t('partnerPortal.program.tierPct')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.minLocations}
                  className={row.current ? 'pp-tier-current' : ''}
                  aria-current={row.current ? 'true' : undefined}
                >
                  <td>
                    {row.maxLocations === null
                      ? t('partnerPortal.program.tierOpen', { min: row.minLocations })
                      : t('partnerPortal.program.tierRange', {
                          min: row.minLocations,
                          max: row.maxLocations,
                        })}
                  </td>
                  <td>{row.pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    );
  }

  const signOn = signOnSummary(partner);
  return (
    <section
      className="pp-card pp-program-card"
      data-program="influencer"
      aria-labelledby="pp-program"
    >
      <h2 id="pp-program" className="pp-card-title">
        {t('partnerPortal.program.influencerTitle')} <HelpIcon topicKey="partners.programs" />
      </h2>
      <p className="pp-program-highlight">
        <span className="pp-program-pct">{partner.commissionPct}%</span>
        <span>
          {t('partnerPortal.program.influencerRule', { months: partner.commissionMonths || 12 })}
        </span>
      </p>
      {signOn && (
        <p className="pp-program-next" data-testid="pp-signon">
          {t('partnerPortal.program.signOnUsed', { used: signOn.used, cap: signOn.cap })}
        </p>
      )}
      <p className="pp-muted">{t('partnerPortal.program.clawback')}</p>
    </section>
  );
}

export default PartnerProgramCard;
